import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useSyncExternalStore,
} from 'react'
import type { ThemeConfig } from 'antd'
import { doc, setDoc } from 'firebase/firestore'
import { useFirestore, useSigninCheck, useUser } from 'reactfire'
import * as Sentry from '@sentry/react'
import {
  buildThemeConfig,
  DEFAULT_THEME_PREFERENCE,
  getAppCssVars,
  type ResolvedTheme,
  THEME_PREFERENCES,
  type ThemePreference,
} from './themeTokens'
import logoLight from '../resources/img/logo.svg'
import logoDark from '../resources/img/logo_dark.svg'
import logoBwLight from '../resources/img/logo_bw.svg'
import logoBwDark from '../resources/img/logo_bw_dark.svg'
import logoNoTextLight from '../resources/img/logo_no_text.svg'
import logoNoTextDark from '../resources/img/logo_no_text_dark.svg'

// Keep the key and the light-only paths in sync with public/theme-init.js,
// which applies the same resolution before the bundle loads.
const STORAGE_KEY = 'theme'
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'
/** Brand-designed public pages that always render light. */
const LIGHT_ONLY_PATHS = ['/qualification']

export function asThemePreference(value: unknown): ThemePreference {
  return (THEME_PREFERENCES as readonly unknown[]).includes(value)
    ? (value as ThemePreference)
    : DEFAULT_THEME_PREFERENCE
}

function readSavedPreference(): ThemePreference {
  try {
    return asThemePreference(localStorage.getItem(STORAGE_KEY))
  } catch {
    return DEFAULT_THEME_PREFERENCE
  }
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia(SYSTEM_DARK_QUERY).matches === true
  } catch {
    return false
  }
}

function isLightOnlyRoute(): boolean {
  return LIGHT_ONLY_PATHS.some((path) =>
    window.location.pathname.startsWith(path)
  )
}

function resolve(pref: ThemePreference): ResolvedTheme {
  if (isLightOnlyRoute()) return 'light'
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light'
  return pref
}

let preference: ThemePreference = readSavedPreference()
let resolved: ResolvedTheme = resolve(preference)
const listeners = new Set<() => void>()

/** Project the resolved theme onto `<html>` for stylesheets and the UA. */
function applyToDocument(): void {
  const root = document.documentElement
  root.dataset.theme = resolved
  root.style.colorScheme = resolved
  for (const [name, value] of Object.entries(getAppCssVars(resolved))) {
    root.style.setProperty(name, value)
  }
}

function refresh(): void {
  resolved = resolve(preference)
  applyToDocument()
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

applyToDocument()

// Follow the OS setting live while the preference is `system`.
try {
  window.matchMedia(SYSTEM_DARK_QUERY).addEventListener('change', refresh)
} catch {
  // matchMedia unavailable: `system` simply resolves to light.
}

// Keep other tabs of the same browser in step.
window.addEventListener('storage', (event) => {
  if (event.key !== null && event.key !== STORAGE_KEY) return
  preference = readSavedPreference()
  refresh()
})

/**
 * Switch the active theme: persist to `localStorage` (the source
 * public/theme-init.js reads on next load) and re-theme the app live.
 */
export function setAppTheme(next: ThemePreference): void {
  preference = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Ignore storage failures (private mode, etc.); the in-memory switch still works.
  }
  refresh()
}

/** The user's choice: `light`, `dark` or `system`. */
export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, () => preference)
}

/** The colour scheme actually rendered, with `system` resolved. */
export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, () => resolved)
}

/** The antd theme for the active colour scheme. Source for the root `ConfigProvider`. */
export function useAppThemeConfig(): ThemeConfig {
  const mode = useResolvedTheme()
  return useMemo(() => buildThemeConfig(mode), [mode])
}

const LOGOS = {
  full: { light: logoLight, dark: logoDark },
  bw: { light: logoBwLight, dark: logoBwDark },
  noText: { light: logoNoTextLight, dark: logoNoTextDark },
} as const

/** The logo asset that is legible on the active colour scheme. */
export function useLogo(variant: keyof typeof LOGOS) {
  return LOGOS[variant][useResolvedTheme()]
}

/**
 * Render the page in the light theme regardless of the preference, for the
 * routes in {@link LIGHT_ONLY_PATHS}. The route decides; this hook only
 * re-evaluates on mount and unmount, since the store cannot observe
 * client-side navigation by itself.
 */
export function useLightOnlyRoute(): void {
  useLayoutEffect(() => {
    refresh()
    return refresh
  }, [])
}

function useIsImpersonating(): boolean {
  const { data: signInCheck } = useSigninCheck({
    requiredClaims: { impersonating: true },
  })
  return signInCheck?.hasRequiredClaims === true
}

/**
 * Returns a setter that switches the theme AND persists it to the user's
 * account (`userprofiles/{uid}.theme`), so the preference follows the account
 * across devices. The Firestore write is best-effort and never blocks the UI.
 *
 * During impersonation the switch stays session-local, for the same reason as
 * the language preference: the signed-in uid is the customer's, and persisting
 * would overwrite the customer's own choice (CL-2059).
 */
export function useSetTheme(): (next: ThemePreference) => void {
  const firestore = useFirestore()
  const { data: user } = useUser()
  const uid = user?.uid ?? null
  const isImpersonating = useIsImpersonating()

  return useCallback(
    (next: ThemePreference) => {
      setAppTheme(next)
      if (uid && !isImpersonating) {
        void setDoc(
          doc(firestore, 'userprofiles', uid),
          { theme: next, updatedAt: Date.now() },
          { merge: true }
        ).catch((error) => {
          Sentry.captureException(error, {
            tags: { area: 'theme', action: 'persist_theme' },
            extra: { theme: next },
          })
        })
      }
    },
    [firestore, uid, isImpersonating]
  )
}

/**
 * Apply the account's stored theme on sign-in. A loaded profile without the
 * field reads as the light default, so a preference left in `localStorage` by
 * another account on a shared browser does not carry over. Skipped during
 * impersonation: the impersonator keeps their own theme (CL-2062).
 */
export function useApplyProfileTheme({
  profileLoaded,
  theme,
}: {
  profileLoaded: boolean
  theme: string | undefined
}): void {
  const isImpersonating = useIsImpersonating()

  useEffect(() => {
    if (!profileLoaded || isImpersonating) return
    const target = asThemePreference(theme)
    if (preference !== target) {
      setAppTheme(target)
    }
  }, [profileLoaded, theme, isImpersonating])
}
